/**
 * ⚠️  DESIGN LOCKED COMPONENT - DO NOT MODIFY DIMENSIONS ⚠️
 * ========================================================
 * 
 * This hero section's design is LOCKED by user requirements.
 * The following elements CANNOT be changed:
 * 
 * 🔒 LOCKED ELEMENTS:
 * - Section height: min-h-screen
 * - Container width: max-w-6xl (stats), max-w-4xl (content)
 * - Typography: text-5xl md:text-7xl lg:text-8xl font-thin
 * - Spacing: pt-16 md:pt-20 lg:pt-24, mb-12 md:mb-16, mb-6, mb-12, mb-16
 * - Background: bg-gradient-to-b from-white via-gray-50 to-white
 * - Stats grid: grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6
 * - Button dimensions and styling
 * - All responsive breakpoints and scaling
 * 
 * ⚠️  MODIFICATION PROHIBITED WITHOUT EXPLICIT APPROVAL ⚠️
 */

import React from 'react';
import { Button } from "@/components/ui/button";
import { ArrowRight, Play, Brain, Zap, Database, Headphones, ShieldCheck, Workflow } from 'lucide-react';
import { Link } from 'react-router-dom';
import { VideoModal } from "@/components/ui/video-modal";
import { PageBadge } from "@/components/ui/page-badge";
import { ProfessionalBadge } from "@/components/ui/professional-badge";
import { DESIGN_LOCK } from "@/lib/designLock";
import { useLocale } from '@/contexts/LocaleContext';
import { HeroImage } from '@/components/performance/OptimizedImage';
export const AppleHero = () => {
  const [isVideoModalOpen, setIsVideoModalOpen] = React.useState(false);
  const {
    convertText
  } = useLocale();
  return <>
      {/* 🔒 DESIGN LOCKED: Section dimensions - DO NOT MODIFY */}
      <section className="relative min-h-screen flex items-start justify-center overflow-hidden pt-16 md:pt-20 lg:pt-24">
        {/* Background Image - Correct mountain landscape */}
        <HeroImage
          src="https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05"
          alt="Inspiring mountain landscape with natural beauty"
          sizes="100vw"
          overlayClassName=""
        />
        
        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="max-w-6xl mx-auto">
            {/* Clean PageBadge matching About Us style */}
            <div className="text-center mb-12 md:mb-16">
              <PageBadge variant="gradient" className="mb-6">
                <Brain className="w-4 h-4 mr-2" />
                {convertText('Next Generation AI Platform')}
              </PageBadge>
              
              {/* Simplified headline matching About Us typography */}
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-thin text-gray-900 mb-6 tracking-tight leading-none">
                Revolutionise your business{' '}
                <br />
                <span className="font-medium text-blue-900">with AI + Human excellence.</span>
              </h1>
              
              {/* Clean subheadline with updated text */}
              <p className="text-xl md:text-2xl lg:text-3xl text-blue-900 md:text-white font-light max-w-4xl mx-auto leading-relaxed mb-12">We design, build, and run the connected technology you need to manage your business and customer experience, from discovery through ongoing support.</p>
            </div>
            
            {/* Single prominent CTA */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
              <Link to="/website-wizard">
                <Button size="lg" className="text-lg px-10 py-5 h-14 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 btn-text-white border-2 border-transparent hover:border-white rounded-full font-medium transition-all duration-300 shadow-lg hover:shadow-xl">
                  <Zap className="mr-3 h-6 w-6" />
                  Start Your Project
                  <ArrowRight className="ml-3 h-6 w-6" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" onClick={() => setIsVideoModalOpen(true)} className="text-lg px-10 py-5 h-14 bg-white border-2 border-blue-600 text-blue-600 hover:bg-white hover:text-blue-600 hover:border-blue-600 rounded-full font-medium transition-all duration-300 shadow-lg hover:scale-105 backdrop-blur-sm">
                <Play className="mr-3 h-6 w-6" />
                Watch Demo
              </Button>
            </div>

            {/* Professional badges matching About Us style */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-20 pt-12 border-b pb-20">
              <ProfessionalBadge icon={Workflow} title="Design, Build & Run" variant="orange" textColor="light" />
              <ProfessionalBadge icon={ShieldCheck} title="Secure Cloud Delivery" variant="green" textColor="light" />
              <ProfessionalBadge icon={Database} title="Connected Business Data" variant="blue" textColor="light" />
              <ProfessionalBadge icon={Headphones} title="Ongoing Expert Support" variant="purple" textColor="light" />
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      <VideoModal isOpen={isVideoModalOpen} onClose={() => setIsVideoModalOpen(false)} videoUrl="https://youtu.be/yqWX86uT5jM?si=QEeQgSiS1kf2ayJ0" title="Dialgia Group AI Platform Demo" />
    </>;
};
